<template>
  <div>
    <NavBar />
    <div class="page-wrap">
      <h2 class="page-title">Όλες οι Αιτήσεις</h2>

      <v-card class="filters-card" elevation="1">
        <v-row dense align="center">
          <v-col cols="12" md="6">
            <v-select
              v-model="selectedInvitationId"
              :items="invitationOptions"
              item-text="text"
              item-value="value"
              label="Πρόσκληση"
              dense outlined clearable hide-details
              @change="onFilterChange"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="selectedJobRoleId"
              :items="jobRoleOptions"
              item-text="text"
              item-value="value"
              label="Θέση"
              dense outlined clearable hide-details
              @change="onFilterChange"
            />
          </v-col>
        </v-row>
      </v-card>

      <v-card class="results-card" elevation="1">
        <v-data-table
          :headers="headers"
          :loading="loading"
          :items="items"
          item-key="id"
          :server-items-length="totalItems"
          @pagination="onPagination"
          :footer-props="{ itemsPerPageOptions: [5, 10, 20, 50, 100] }"
        >
          <template v-slot:item.candidate="{ item }">
            <div>
              <strong>{{ item.userLastName }} {{ item.userFirstName }}</strong>
              <div class="muted">{{ item.userEmail }}</div>
              <div class="muted" v-if="item.mobilePhone">{{ item.mobilePhone }}</div>
            </div>
          </template>

          <template v-slot:item.jobRoleNames="{ item }">
            <div class="role-chips">
              <v-chip
                v-for="(name, idx) in (item.jobRoleNames || [])"
                :key="idx"
                :color="roleChipColor(name)"
                dark
                small
                class="role-chip"
              >
                {{ name }}
              </v-chip>
              <span v-if="!item.jobRoleNames || !item.jobRoleNames.length" class="muted">—</span>
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <div class="actionButs">
              <v-btn
                color="primary"
                small
                :loading="downloadingId === item.id"
                @click="downloadAll(item)"
              >
                <v-icon left small>mdi-download</v-icon>
                Κατέβασμα
              </v-btn>
              <v-btn
                v-if="isAdmin"
                color="error"
                small
                :loading="deletingId === item.id"
                @click="deleteSubmission(item)"
              >
                <v-icon left small>mdi-delete</v-icon>
                Διαγραφή
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import NavBar from "./NavBar.vue";
import axios from "axios";
import Swal from "sweetalert2";
import JSZip from "jszip";
import store from "../state/store";

export default {
  name: "AllSubmissions",
  components: { NavBar },
  data() {
    return {
      searchQuery: "",
      items: [],
      totalItems: 0,
      totalPages: 0,
      loading: true,
      downloadingId: null,
      deletingId: null,
      currentPage: 1,
      itemsPerPage: 10,
      searchDebounce: null,
      invitations: [],
      selectedInvitationId: null,
      selectedJobRoleId: null,
      headers: [
        { text: "Πρόσκληση", value: "invitationName" },
        { text: "Υποψήφιος", value: "candidate", sortable: false },
        { text: "Θέσεις", value: "jobRoleNames", sortable: false },
        { text: "Αρχείο", value: "underFileName", sortable: false },
        { text: "Δράσεις", value: "actions", sortable: false, align: "center" },
      ],
    };
  },
  computed: {
    isAdmin() {
      return this.$store.getters['user/getUserRole'] === 'admin';
    },
    invitationOptions() {
      return (this.invitations || []).map(inv => ({
        text: inv.invName,
        value: inv.invitationId,
      }));
    },
    jobRoleOptions() {
      // If an invitation is selected, show only its job roles.
      // Otherwise show every job role across every invitation, suffixed
      // with the invitation name so the user can disambiguate.
      const invs = this.invitations || [];
      if (this.selectedInvitationId) {
        const inv = invs.find(i => i.invitationId === this.selectedInvitationId);
        if (!inv || !inv.jobRoles) return [];
        return inv.jobRoles.map(jr => ({
          text: jr.jobRoleName,
          value: jr.jobRoleId,
        }));
      }
      const opts = [];
      for (const inv of invs) {
        if (!inv.jobRoles) continue;
        for (const jr of inv.jobRoles) {
          opts.push({
            text: `${jr.jobRoleName} — ${inv.invName}`,
            value: jr.jobRoleId,
          });
        }
      }
      return opts;
    }
  },
  mounted() {
    this.fetchInvitations();
  },
  methods: {
    roleChipColor(name) {
      // Stable per-name color from a curated Material palette.
      const palette = [
        'indigo darken-1', 'teal darken-1', 'deep-orange darken-1',
        'green darken-2', 'purple darken-1', 'blue-grey darken-1',
        'pink darken-1', 'brown darken-1', 'cyan darken-2',
        'amber darken-3', 'red darken-1', 'light-green darken-2',
      ];
      const s = String(name || '');
      let hash = 0;
      for (let i = 0; i < s.length; i++) {
        hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
      }
      return palette[hash % palette.length];
    },
    async fetchInvitations() {
      try {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + store.state.user.token;
        const { data } = await axios.get(
          axios.defaults.baseURL + "/invitation/invitationPaged",
          { params: { page: 0, size: 1000 } }
        );
        this.invitations = data.content || [];
      } catch (e) {
        console.error("Failed to load invitations", e);
      }
    },

    onFilterChange() {
      // Reset job-role filter when the invitation changes to one that
      // doesn't include the previously selected role.
      if (!this.jobRoleOptions.find(o => o.value === this.selectedJobRoleId)) {
        this.selectedJobRoleId = null;
      }
      this.fetchPage(1, this.itemsPerPage);
    },
    onPagination({ page, itemsPerPage = 10 }) {
      this.currentPage = page;
      this.itemsPerPage = itemsPerPage;
      this.fetchPage(page, itemsPerPage);
    },

    onSearchInput() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.fetchPage(1, this.itemsPerPage);
      }, 300);
    },

    async fetchPage(page, itemsPerPage) {
      this.loading = true;
      try {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + store.state.user.token;
        const params = {
          query: this.searchQuery || "",
          page: page - 1,
          size: itemsPerPage,
        };
        if (this.selectedInvitationId) params.invitationId = this.selectedInvitationId;
        if (this.selectedJobRoleId) params.jobRoleId = this.selectedJobRoleId;
        const response = await axios.get(
          axios.defaults.baseURL + "/submission/allPaged",
          { params }
        );
        this.items = response.data.content;
        this.totalPages = response.data.totalPages;
        this.totalItems = response.data.totalElements;
      } catch (error) {
        console.error(error);
        await this.alert("error", "Σφάλμα", "Αποτυχία ανάκτησης αιτήσεων");
      } finally {
        this.loading = false;
      }
    },

    async deleteSubmission(item) {
      const confirm = await Swal.fire({
        icon: "warning",
        title: "Διαγραφή Αίτησης",
        html:
          "Θέλετε σίγουρα να διαγράψετε την αίτηση <b>#" + item.id + "</b><br/>" +
          "του υποψηφίου <b>" +
          ((item.userLastName || "") + " " + (item.userFirstName || "")).trim() +
          "</b>;<br/>Η ενέργεια δεν αναιρείται.",
        showCancelButton: true,
        confirmButtonText: "Ναι, διαγραφή",
        cancelButtonText: "Άκυρο",
        confirmButtonColor: "#d33",
      });
      if (!confirm.isConfirmed) return;

      this.deletingId = item.id;
      try {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + store.state.user.token;
        await axios.delete(
          axios.defaults.baseURL + "/submission/" + item.id
        );
        await this.alert("success", "Η αίτηση διαγράφηκε", "");
        await this.fetchPage(this.currentPage, this.itemsPerPage);
      } catch (error) {
        console.error(error);
        const status = error && error.response && error.response.status;
        await this.alert(
          "error",
          "Σφάλμα Διαγραφής",
          status === 403
            ? "Δεν έχετε δικαίωμα διαγραφής."
            : "Η διαγραφή απέτυχε."
        );
      } finally {
        this.deletingId = null;
      }
    },

    async downloadAll(item) {
      this.downloadingId = item.id;
      try {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + store.state.user.token;

        const zip = new JSZip();

        // 1) Candidate details as a plain-text file.
        zip.file("στοιχεια.txt", this.buildDetailsText(item));

        // 2) The uploaded file, if any.
        if (item.underFileName) {
          try {
            const { data } = await axios.get(
              axios.defaults.baseURL + "/user/downloadUnderFile/" + item.id,
              { responseType: "blob" }
            );
            zip.file(item.underFileName, data);
          } catch (e) {
            console.error("Could not fetch uploaded file", e);
            zip.file(
              "σφαλμα_ληψης_αρχειου.txt",
              "Η λήψη του αρχείου '" + item.underFileName + "' απέτυχε."
            );
          }
        }

        const blob = await zip.generateAsync({ type: "blob" });
        const baseName = this.sanitize(
          "αιτηση_" +
            item.id +
            "_" +
            (item.userLastName || "user") +
            "_" +
            (item.userFirstName || "")
        );
        this.triggerBlobDownload(blob, baseName + ".zip");
      } catch (error) {
        console.error(error);
        await this.alert("error", "Σφάλμα Λήψης", "Δεν ήταν δυνατή η δημιουργία του αρχείου.");
      } finally {
        this.downloadingId = null;
      }
    },

    buildDetailsText(item) {
      const lines = [];
      lines.push("ΔΙΑΧΕΙΡΙΣΗ ΑΙΤΗΣΕΩΝ ΣΣΕ — Στοιχεία Αίτησης");
      lines.push("=".repeat(60));
      lines.push("");
      lines.push("Αιτηση");
      lines.push("------");
      lines.push("ID αίτησης:        " + (item.id || ""));
      lines.push("Κωδικός επιβεβαίωσης: " + (item.invHash || ""));
      lines.push("Πρόσκληση:          " + (item.invitationName || "") +
                 " (ID: " + (item.invitationId || "") + ")");
      lines.push("Αρχείο:              " + (item.underFileName || "—"));
      lines.push("Θέσεις:             " +
                 ((item.jobRoleNames && item.jobRoleNames.length) ?
                   item.jobRoleNames.join(", ") : "—"));
      lines.push("");
      lines.push("Υποψήφιος");
      lines.push("----------");
      lines.push("ID χρήστη:          " + (item.userId || ""));
      lines.push("Username:            " + (item.userName || ""));
      lines.push("Όνομα:              " + (item.userFirstName || ""));
      lines.push("Επώνυμο:            " + (item.userLastName || ""));
      lines.push("Email:               " + (item.userEmail || ""));
      lines.push("Κινητό:             " + (item.mobilePhone || ""));
      lines.push("");
      lines.push("Λήψη παραχθείσα: " + new Date().toISOString());
      // Use \r\n so Windows Notepad renders correctly.
      return lines.join("\r\n");
    },

    triggerBlobDownload(blob, filename) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },

    sanitize(name) {
      return (name || "")
        .toString()
        .replace(/[^\p{L}\p{N}._-]+/gu, "_")
        .replace(/^_+|_+$/g, "");
    },

    alert(icon, title, text) {
      return Swal.fire({
        icon,
        title,
        text,
        timer: 2500,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    },
  },
};
</script>

<style scoped>
.page-wrap {
  max-width: 1500px;
  margin: 0 auto;
  padding: 20px 24px 40px;
}
.page-title {
  font-weight: 600;
  padding: 10px 0 20px;
}
.filters-card {
  padding: 16px 16px 8px;
  margin-bottom: 20px;
  border-radius: 8px;
}
.results-card {
  border-radius: 8px;
  padding: 4px;
}
.muted {
  color: #666;
  font-size: 12px;
}
.role-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 0;
  max-width: 480px;
}
.role-chip {
  font-weight: 500;
  height: auto !important;
  min-height: 24px;
  white-space: normal !important;
  padding: 4px 10px !important;
}
.role-chip >>> .v-chip__content {
  white-space: normal;
  line-height: 1.2;
  padding: 2px 0;
}
.actionButs {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
}
.results-card >>> .v-data-table__wrapper {
  overflow-x: hidden;
}
.results-card >>> table {
  table-layout: auto;
}
.results-card >>> td {
  vertical-align: middle;
  word-break: break-word;
}
</style>
